import { debounce, invokeLater } from '../../../CodesThatsGottaGoSomewhereElse';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Client, ClientManager, Communicator } from '../../../concepts/objects';
import { Point } from '../../../platform/geom/Point';
import { Camera, CameraState } from '../../canvas/Camera';
import { EditorSession } from '../../canvas/EditorSession';
import { Burdens } from '../../canvas/extensibility/Burdens';
import { MyRoleInfo } from '../../canvas/MyRoleInfo';
import { WidgetManager } from '../../canvas/workspace/widgets/WidgetManager';
import { ActiveObject, resolve } from '../../Container';
import { alert } from '../alerts/AlertCommands';
import { IncognitoInfo } from '../incognito/IncognitoInfo';
import { ToastHub } from '../toast/ToastHub';
import { FollowersInfo } from './FollowersInfo';
import { FollowingInfo } from './FollowingInfo';
import { FollowWidget } from './FollowWidget';

export class FollowMechanics extends ActiveObject {

    @resolve(Camera) private camera!: Camera;
    @resolve(ClientManager) private clients!: ClientManager;
    @resolve(CommandDispatcher) private dispatch!: CommandDispatcher;
    @resolve(Communicator) private comms!: Communicator;
    @resolve(EditorSession) private session!: EditorSession;
    @resolve(ToastHub) private toast!: ToastHub;
    @resolve(WidgetManager) private widgets!: WidgetManager;

    private hooks = null as Burdens | null;

    public start(): void {
        this.realizeWidgetIfApplicable();
        this.burden(
            this.camera.observe(this.submitCameraState),
            this.clients.on('update', this.onClientUpdate),
            this.clients.on('leave', this.onClientLeave),
            // this.comms.recv('her')
            this.session.observe(FollowingInfo, this.syncFollowing),
            this.session.observe(IncognitoInfo, this.syncFollowing),
            this.session.observe(MyRoleInfo, this.realizeWidgetIfApplicable),
            () => this.hooks?.destroy(),
        );
    }

    private syncFollowing = () => {
        const following = this.session.get(FollowingInfo);
        const incognito = this.session.get(IncognitoInfo);

        if (!!following.userId && !!incognito.enabled) {
            invokeLater(() => this.session.update(FollowingInfo, FollowingInfo.empty));
            this.dispatch(alert, ['Following is not available during Anonymous Mode.']);
        }
        else {
            // Set who we are following in our profile
            this.clients.self.patch({ following: following.userId || null });
            if (!!following.userId) {
                // Update to where they are
                this.applyFollowState(this.clients.others.find(x => x.userId === following.userId));
                // Notify the follower
                const recipients = this.clients.all.filter(x => x.userId === following.userId);
                for (const r of recipients) {
                    this.toast.send(r.id, 'ptpt:follow', {});
                }
            }
        }

    };

    private onClientUpdate = ([client, keys]: [Client, string[]]) => {

        // If we are following this client and it's their camera state...
        if (keys.includes('cs') && client.userId === this.session.get(FollowingInfo).userId) {
            this.applyFollowState(client);
        }

        // If their following changed, update our followers
        if (keys.includes('following')) {
            this.syncFollowers();
        }

    };

    private onClientLeave = (client: Client) => {
        const followers = this.session.get(FollowersInfo);
        const following = this.session.get(FollowingInfo);

        // If we are following this client, unfollow.
        if (client.userId === following.userId) {
            this.session.update(FollowingInfo, FollowingInfo.empty);
        }

        // If they were following us, sync
        if (followers.userIds.includes(client.userId)) {
            this.syncFollowers();
        }
    };

    private realizeWidgetIfApplicable = () => {

        this.widgets.delete('following');

        const { canParticipate } = this.session.get(MyRoleInfo);
        if (!canParticipate) return;

        const cont = this.widgets.createContainer({
            name: 'following',
            position: ['docked', 'topRight'],
            style: 'none',
            weight: 300,
        });

        cont.add(this.widgets.createInline({
            ui: FollowWidget
        }));

        this.widgets.add(cont);
    };

    private syncFollowers = () => {
        const userIds = this.clients.others
            .filter(x => x.get('following') === this.clients.self.userId)
            .map(x => x.userId);
        this.session.patch(FollowersInfo, { userIds });
    };

    private submitCameraState = (cs: CameraState) => {
        this.clients.self.patch({ cs: JSON.stringify([cs.position.x, cs.position.y, cs.scale]) });
    };

    private applyFollowState(client: Client) {
        if (!client) return;
        const [cx, cy, cs] = JSON.parse(client.get('cs')?.toString() || '[]');
        if (typeof (cx) !== 'number') return;
        if (typeof (cy) !== 'number') return;
        if (typeof (cs) !== 'number') return;
        this.applyCameraView(cx, cy, cs);
    }

    private applyCameraView = debounce((cx: number, cy: number, cs: number) =>
        this.camera.animateTo(new Point(cx, cy), cs, 80),
        40,
        true);
}
